import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { cx } from "@/lib/cx";

/**
 * Heading of a stacked section on a wide page (tables, lists, previews): title (16px / 600), an
 * optional 16px glyph, one sentence of description (max 672px so it stays readable next to a wide
 * table), and actions on the right that line up with the last line of text. Settings-style pages
 * with a label column use FormSection instead.
 *
 *   <section aria-labelledby="gh-linked" className="flex flex-col gap-3">
 *     <SectionHeader id="gh-linked" title={m.adm_gh_linked_title()} description={m.adm_gh_linked_body()}
 *       actions={<Button>New</Button>} />
 *     <DataTable ... />
 *   </section>
 *
 * The page's h1 lives in PageHeader, so sections are h2 (h3 inside a drawer or a nested section).
 */
export function SectionHeader({
  id,
  title,
  description,
  icon: I,
  actions,
  level = 2,
  className,
}: {
  /** Pass it to the section's aria-labelledby. */
  id?: string;
  title: ReactNode;
  description?: ReactNode;
  icon?: Icon;
  actions?: ReactNode;
  level?: 2 | 3;
  className?: string;
}) {
  const H = level === 2 ? "h2" : "h3";
  return (
    <div className={cx("flex flex-wrap items-end justify-between gap-x-4 gap-y-2", className)}>
      <div className="min-w-0 flex-1 basis-72">
        <H id={id} className="flex min-w-0 items-center gap-2 text-base font-semibold tracking-snug text-fg">
          {I ? <I size={16} aria-hidden="true" className="shrink-0 text-fg-subtle" /> : null}
          {title}
        </H>
        {description ? <p className="mt-0.5 max-w-2xl text-sm text-pretty text-fg-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2">{actions}</div> : null}
    </div>
  );
}
